/**
 * System Settings KV — backend registry'den canlı çekilen toggle'lar.
 *
 * Setting tipine göre: bool → Switch, str/choices → Select, int → NumberInput,
 * dict → JSON Textarea (placeholder), null → default'a dön.
 */
import { Card, Stack, Group, Text, Switch, NumberInput, Select, Loader, Center, Badge, Button, TextInput, Tooltip } from "@mantine/core";
import { IconDeviceFloppy, IconInfoCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../shared/api";
import { PageHeader, EmptyState } from "../../../shared/ui";

interface RegistryItem {
  key: string;
  type: string;
  default: any;
  current: any;
  choices?: string[];
}

// ---------------------------------------------------------------------------
// TR etiket sözlüğü — backend registry.py'deki key'ler için kullanıcı dostu
// görünüm. Yeni key eklendiğinde buraya da eklensin. Eşleşme yoksa key kendisi
// gösterilir (geriye dönük uyum).
// ---------------------------------------------------------------------------

const GROUP_LABELS: Record<string, string> = {
  qr: "QR Menü",
  loyalty: "Sadakat Programı",
  leaderboard: "Liderlik Tablosu",
  badges: "Rozetler",
  waiter: "Garson",
  tips: "Bahşiş",
  employee: "Ayın Personeli",
  reservation: "Rezervasyon",
  cashier: "Gün Sonu Kapanış",
  modifier: "Modifier Filtreleme",
  adjustment: "Stok Düzeltme Onayı",
  waste: "Fire Onayı",
  kitchen: "Mutfak / Otomatik Basım",
  stock: "Kritik Stok Bildirimi",
  recipe: "Reçete Stoğu",
  table: "Masa / Salon",
  po: "Satınalma / Mal Kabul",
};

const GROUP_ORDER = ["qr", "loyalty", "leaderboard", "badges", "waiter", "tips", "employee", "reservation", "cashier", "modifier", "adjustment", "waste", "kitchen", "stock", "recipe", "table", "po"];

interface KeyMeta {
  label: string;
  description?: string;
  choiceLabels?: Record<string, string>;
}

const KEY_META: Record<string, KeyMeta> = {
  // QR Menü
  qr_order_enabled: {
    label: "QR menüden sipariş alma açık",
    description: "Kapalıyken QR menüsü sadece görüntüleme amaçlı kalır.",
  },
  qr_require_waiter_approval: {
    label: "QR siparişinde garson onayı gerekli",
    description: "Kapalıyken müşteri siparişi doğrudan mutfağa düşer (DRAFT atlanır).",
  },
  qr_payment_mode: {
    label: "QR ödeme modu",
    description: "Masada müşteri ödeme şekli.",
    choiceLabels: {
      NONE: "Yok (sadece sipariş)",
      CALL_WAITER: "Garson çağır",
      DIGITAL: "Dijital ödeme",
    },
  },
  qr_show_prices: {
    label: "QR menüde fiyat göster",
  },
  qr_default_lang: {
    label: "QR menü varsayılan dili",
    description: "Müşteri bir dil seçmeden gelen ilk menü dili.",
    choiceLabels: { tr: "Türkçe", en: "English" },
  },
  qr_languages_enabled: {
    label: "QR menüde sunulan diller",
    description: "Müşteriye dil değiştiricide gösterilecek diller (tr, en).",
  },
  qr_hide_name_when_logo: {
    label: "Logo varken restoran adını gizle",
    description: "Logo yüklüyse açılış ekranında restoran adı yazısı gösterilmez (çift marka önlenir).",
  },
  qr_card_blur: {
    label: "Açılış kartına blur (buzlu cam) efekti",
    description: "Arka plan görseli açılış kartının arkasından bulanık görünür.",
  },
  qr_card_opacity: {
    label: "Açılış kartı yoğunluğu (%)",
    description: "Blur açıkken saydamlık + bulanıklığı birlikte ayarlar. 0 = tam şeffaf + hiç blur yok, 100 = tam opak + tam blur.",
  },
  qr_show_reservation: {
    label: "Açılışta rezervasyon butonu",
    description: "QR menü açılışında 'Rezervasyon Yap' butonu gösterilsin mi?",
  },

  // Sadakat
  loyalty_enabled: { label: "Sadakat programı aktif" },
  loyalty_mode: {
    label: "Sadakat modu",
    choiceLabels: {
      DISCOUNT: "İndirim bazlı",
      TIER: "Seviye bazlı",
      COUPON: "Kupon bazlı",
      MIXED: "Karma",
    },
  },
  loyalty_point_per_lira: {
    label: "Lira başına kazanılan puan",
    description: "1 TL harcamada müşteri kaç puan kazansın?",
  },
  loyalty_point_value_minor: {
    label: "1 puanın değeri (₺)",
    description: "Örn. 0,10 ₺ → 100 puan = 10 ₺.",
  },
  loyalty_tier_thresholds: { label: "Sadakat seviyeleri eşikleri" },
  loyalty_tier_discount_percent: { label: "Seviye bazlı indirim oranları (%)" },

  // Liderlik
  leaderboard_enabled: { label: "Liderlik tablosu aktif" },
  leaderboard_period: {
    label: "Liderlik dönemi",
    choiceLabels: {
      WEEKLY: "Haftalık",
      MONTHLY: "Aylık",
      ALL_TIME: "Tüm zamanlar",
    },
  },

  // Rozetler
  badges_enabled: { label: "Rozetler aktif" },

  // Garson
  waiter_commission_percent: {
    label: "Garson prim oranı (%)",
    description: "Decimal yazım, örn. 5.0 = %5.",
  },
  waiter_ratings_enabled: {
    label: "Garson puanlama açık",
    description: "QR ile giriş yapan müşteri garsonu puanlayabilir.",
  },

  // Bahşiş
  tips_enabled: { label: "Bahşiş açık" },

  // Personel
  employee_of_month_enabled: { label: "Ayın personeli özelliği açık" },
  employee_of_month_show_in_qr: {
    label: "Ayın personelini QR menüde göster",
    description: "Müşteriler QR menüden ayın personelini görsün mü?",
  },

  // Rezervasyon
  reservation_enabled: {
    label: "Masa rezervasyonu aktif",
    description: "Kapalıyken rezervasyon almazsınız.",
  },
  reservation_deposit_required: {
    label: "Kapora zorunlu",
    description: "Kapalıyken müşteri ödeme yapmadan rezervasyon onaylanır.",
  },
  reservation_deposit_per_person_minor: {
    label: "Kişi başı kapora (₺)",
    description: "Örn. 100 ₺.",
  },
  reservation_grace_period_minutes: {
    label: "Geç gelme toleransı (dk)",
    description: "Saatten N dk sonra gelmeyen otomatik NO_SHOW (gelmedi) olur.",
  },
  reservation_default_duration_minutes: {
    label: "Varsayılan rezervasyon süresi (dk)",
    description: "Bir masada müşteri kaç dakika oturur (varsayılan).",
  },
  reservation_show_guests_to_others: {
    label: "Misafirleri diğer müşterilere göster",
    description: "Misafirlerin de ayrıca onayı gerekir (is_visible_to_others).",
  },
  reservation_no_show_refund_mode: {
    label: "Gelmeyen müşteri kapora politikası",
    description: "Müşteri rezervasyona gelmezse kaporaya ne olur?",
    choiceLabels: {
      FORFEIT: "Kapora yanar (restoranda kalır)",
      FULL: "Tam iade",
      PARTIAL: "Yarı iade (%50)",
    },
  },
  reservation_cancel_refund_mode: {
    label: "İptal eden müşteri kapora politikası",
    description: "Müşteri haber verip iptal ederse kaporaya ne olur?",
    choiceLabels: {
      FORFEIT: "Kapora yanar",
      FULL: "Tam iade",
      PARTIAL: "Yarı iade (%50)",
    },
  },

  // Gün sonu kapanış
  cashier_close_hour: {
    label: "Gün sonu kapanış saati (24h)",
    description: "Z raporunun otomatik tetikleneceği saat. Varsayılan 04:00.",
  },
  cashier_pause_orders_on_close: {
    label: "Kapanışta yeni sipariş kabulünü duraklat",
    description: "Açıkken Z raporu istendiğinde açık siparişler bitene kadar yeni sipariş alınmaz.",
  },
  cashier_in_closing_state: {
    label: "Şu an kapanış sürecinde",
    description: "Runtime bayrağı — Z raporu tetiklendiğinde otomatik açılır, manuel değiştirmeyin.",
  },

  // Modifier filtreleme
  modifier_filter_mode: {
    label: "Modifier eşleme modu",
    description:
      "POS'ta ürün seçildiğinde gösterilecek modifier'lar nasıl filtrelensin? " +
      "Ürün bazlı = Modifier 'Ürünler' alanı. Kategori bazlı = Modifier 'Kategoriler' alanı. " +
      "İkisi de = birleşim (önerilen).",
    choiceLabels: {
      PRODUCT: "Ürün bazlı",
      CATEGORY: "Kategori bazlı",
      BOTH: "İkisi de (birleşim)",
    },
  },

  // Otomatik mutfak fişi basımı
  kitchen_auto_print_on_confirm: {
    label: "Onayda mutfak fişi otomatik basılsın",
    description:
      "Sipariş onaylandığında ilgili KDS istasyonunun yazıcısına mutfak fişi " +
      "otomatik basılır. Masa adı + ürün + modifier + sipariş notu fişe yazılır " +
      "ki garson karıştırmasın. Kapalıysa fiş basılmaz; yalnız KDS ekranına düşer. " +
      "İstasyon × yazıcı eşlemesi için: Menü → Mutfak İstasyonları.",
  },

  // Stok düzeltme onayı
  adjustment_approval_threshold_minor: {
    label: "Stok düzeltme onay eşiği (₺)",
    description:
      "Manuel stok düzeltmesi bu parasal değerin üstündeyse yönetici onayı " +
      "gerekir. 0 ₺ = her düzeltme onaya gider.",
  },
  recipe_stock_enforcement: {
    label: "Reçete stoğu zorunlu",
    description:
      "Açıkken hammadde stoğu yetmeyen reçeteli ürün satılamaz. Stok takibi " +
      "yapmıyorsanız kapatın: satış engellenmez, hammadde yine düşülür (stok eksiye iner).",
  },
  adjustment_approval_enabled: {
    label: "Stok düzeltme onayı aktif",
    description: "Kapalıyken tüm stok düzeltme ve fire kayıtları onaysız işlenir (eski davranış).",
  },

  // Fire onayı
  waste_approval_threshold_minor: {
    label: "Fire onay eşiği (₺)",
    description:
      "Fire kaydı bu parasal değerin üstündeyse yönetici onayı gerekir. " +
      "0 ₺ = her fire onaya gider.",
  },

  // Kritik stok bildirimi
  stock_alert_notify_in_app: {
    label: "Uygulama içi bildirim gönder",
    description: "Kritik stok / stok bitti olaylarında zil ikonu üzerinden uygulama içi bildirim gönderilir.",
  },
  stock_alert_notify_email: {
    label: "E-posta bildirimi gönder",
    description:
      "Kritik stok bildirimi e-posta ile de gönderilsin mi? Açıkken OWNER/MANAGER " +
      "rollerine e-posta atılır. Varsayılan kapalı.",
  },
  stock_alert_target_roles: {
    label: "Bildirim alacak roller",
    description:
      "Kritik stok bildirimini hangi roller alacak. Varsayılan: OWNER, MANAGER, CHEF. " +
      "Boş liste = tüm tenant'a yayın.",
  },

  // Satınalma: fazla teslimat toleransı
  po_over_delivery_tolerance_percent: {
    label: "Fazla teslimat toleransı (%)",
    description:
      "Mal kabulde sipariş edilen miktarın üstüne kabul edilebilir fazla " +
      "teslimat yüzdesi. Örn 10 = 2 kg siparişe en çok 2.2 kg kabul edilir. " +
      "0 = hiç fazla kabul etme (tam sipariş kadar).",
  },
  po_over_delivery_block: {
    label: "Fazla teslimatı engelle",
    description:
      "Tolerans yüzdesini aşan fazla teslimatta mal kabul onayı engellenir " +
      "(kullanıcı miktarı düzeltmeli). Kapalıyken aşıma izin verilir (yalnız kayıt).",
  },

  // Masa / Salon
  table_cleaning_auto_free_minutes: {
    label: "Otomatik temizlik sonrası masayı boşalt (dk)",
    description:
      "0 = temizlik adımı atlanır, ödeme alındığında masa direkt BOŞ olur. " +
      ">0 = masa önce TEMİZLİK durumuna geçer, N dakika sonra otomatik BOŞ yapılır. " +
      "Garson yine 'Temizlik Bitti' diyerek erken boşaltabilir.",
  },
};

function metaFor(key: string): KeyMeta {
  return KEY_META[key] || { label: key };
}

function formatDefault(value: any): string {
  if (value === true) return "açık";
  if (value === false) return "kapalı";
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value || "(boş)";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function SystemSettingsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["settings-registry"],
    queryFn: async () => (await apiClient.get("/api/v1/settings-registry/")).data as RegistryItem[],
  });

  if (isLoading) return <Center h={300}><Loader /></Center>;
  const list = data || [];

  // Grup: key prefix'e göre (loyalty_, leaderboard_ vs.)
  // NOT: qr_ ayarları burada GÖSTERİLMEZ — hepsi "Ayarlar → QR Menü"
  // sekmesinde yönetilir (tek yer, tekrar yok).
  const groups: Record<string, RegistryItem[]> = {};
  for (const item of list) {
    if (item.key.startsWith("qr_")) continue;
    const prefix = item.key.split("_")[0] || "diğer";
    groups[prefix] = groups[prefix] || [];
    groups[prefix].push(item);
  }

  const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a);
    const bi = GROUP_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return (
    <Stack gap="md">
      <PageHeader title="Sistem Ayarları" subtitle={`${list.length} ayar — özellik aç/kapa ve eşik değerler`} />

      {list.length === 0 ? <EmptyState title="Ayar yok" /> : (
        sortedGroupKeys.map((group) => (
          <Card key={group} padding="lg" radius="md" withBorder>
            <Text fw={700} mb="sm" tt="uppercase" c="dimmed" size="sm">
              {GROUP_LABELS[group] || group}
            </Text>
            <Stack gap="sm">
              {groups[group].map((item) => (
                <SettingRow key={item.key} item={item} />
              ))}
            </Stack>
          </Card>
        ))
      )}
    </Stack>
  );
}

function SettingRow({ item }: { item: RegistryItem }) {
  const qc = useQueryClient();
  const [value, setValue] = useState<any>(item.current ?? item.default);
  const [dirty, setDirty] = useState(false);
  const meta = metaFor(item.key);

  useEffect(() => {
    setValue(item.current ?? item.default);
    setDirty(false);
  }, [item.current, item.default]);

  const mutation = useMutation({
    mutationFn: async (newValue: any) =>
      (await apiClient.put(`/api/v1/settings/${item.key}/value/`, { value: newValue })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings-registry"] });
      notifications.show({ message: `${meta.label} güncellendi`, color: "green" });
      setDirty(false);
    },
    onError: (e: any) => notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
  });

  function update(v: any) {
    setValue(v);
    setDirty(true);
  }

  function save() {
    mutation.mutate(value);
  }

  function renderInput() {
    if (item.type === "bool") {
      return <Switch checked={!!value} onChange={(e) => { update(e.currentTarget.checked); mutation.mutate(e.currentTarget.checked); }} />;
    }
    if (item.choices && item.choices.length > 0) {
      const data = item.choices.map((c) => ({
        value: c,
        label: meta.choiceLabels?.[c] ?? c,
      }));
      return <Select data={data} value={value || ""} onChange={(v) => update(v)} w={260} />;
    }
    if (item.type === "int") {
      if (item.key.endsWith("_minor")) {
        return (
          <NumberInput
            value={(Number(value) || 0) / 100}
            onChange={(v) => update(Math.round((Number(v) || 0) * 100))}
            min={0}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator="."
            decimalSeparator=","
            suffix=" ₺"
            w={180}
          />
        );
      }
      return <NumberInput value={Number(value) || 0} onChange={(v) => update(Number(v) || 0)} w={150} />;
    }
    if (item.type === "dict") {
      return <Text size="xs" c="dimmed">JSON (admin paneli üzerinden değişmez)</Text>;
    }
    if (item.type === "list") {
      // Virgülle ayrık liste — backend list bekler, string değil. Boşluklar
      // temizlenir, boş öğeler atılır. Örn: "tr, en" → ["tr","en"].
      const asText = Array.isArray(value) ? value.join(", ") : String(value || "");
      return (
        <TextInput
          value={asText}
          onChange={(e) =>
            update(
              e.currentTarget.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          w={220}
        />
      );
    }
    return <TextInput value={String(value || "")} onChange={(e) => update(e.currentTarget.value)} w={220} />;
  }

  const defaultLabel =
    item.choices && item.choices.length > 0 && typeof item.default === "string"
      ? meta.choiceLabels?.[item.default] ?? item.default
      : item.type === "int" && item.key.endsWith("_minor") && typeof item.default === "number"
        ? `${(item.default / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`
        : formatDefault(item.default);

  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start">
      <Stack gap={2} style={{ flex: 1 }}>
        <Group gap="xs">
          <Text fw={600} size="sm">{meta.label}</Text>
          {meta.description && (
            <Tooltip label={meta.description} multiline w={320} withArrow>
              <IconInfoCircle size={14} style={{ color: "var(--mantine-color-dimmed)" }} />
            </Tooltip>
          )}
          <Badge size="xs" variant="light" color="gray">{item.key}</Badge>
        </Group>
        <Text size="xs" c="dimmed">Varsayılan: {defaultLabel}</Text>
      </Stack>
      <Group gap="xs">
        {renderInput()}
        {dirty && item.type !== "bool" && (
          <Button size="xs" leftSection={<IconDeviceFloppy size={12} />} onClick={save} loading={mutation.isPending}>
            Kaydet
          </Button>
        )}
      </Group>
    </Group>
  );
}
